<script lang="ts" setup>
  import { AgentActionsService, type TAgentAction, ActionTypeEnum } from '@/types/generated'
  import { computed, onMounted, ref } from 'vue'
  import { ExternalLink } from 'lucide-vue-next'

  const props = defineProps({
    agentActionUuid: {
      type: String,
      required: false
    }
  })

  const agentAction = ref<TAgentAction | null>(null)
  const loading = ref(true)
  const error = ref<string | null>(null)

  onMounted(async () => {
    if (props.agentActionUuid) {
      try {
        loading.value = true
        agentAction.value = await AgentActionsService.agentActionsRetrieve({
          uuid: props.agentActionUuid
        })
      } catch (err) {
        console.error('Error fetching agent action:', err)
        error.value = 'Failed to load agent action details'
      } finally {
        loading.value = false
      }
    } else {
      loading.value = false
    }
  })

  const getActionTypeDisplayName = (actionType: ActionTypeEnum): string => {
    const displayNames: Record<ActionTypeEnum, string> = {
      [ActionTypeEnum.DRAFT_WORD_DOCUMENT_TEMPLATE]: 'Draft Word Document',
      [ActionTypeEnum.CASE_ASSIGN_LIFECYCLE_WORKFLOW]: 'Assign Workflow',
      [ActionTypeEnum.SEND_APPOINTMENT_BOOKING_LINK_TO_CLIENT]: 'Send Appointment Booking Link',
      [ActionTypeEnum.SEND_CLIENT_TEMPLATE_EMAIL]: 'Send Client Email',
      [ActionTypeEnum.CASE_ASSIGN_LAWYER]: 'Assign Lawyer',
      [ActionTypeEnum.CASE_ASSIGN_LAW_DOMAIN]: 'Assign Law Domain',
      [ActionTypeEnum.CASE_ASSIGN_STATUS]: 'Assign Status'
    }
    return displayNames[actionType] || actionType
  }

  const actionTypeColor = computed(() => {
    if (!agentAction.value) return 'bg-gray-100 text-gray-800'

    const colorMap: Partial<Record<ActionTypeEnum, string>> = {
      [ActionTypeEnum.DRAFT_WORD_DOCUMENT_TEMPLATE]: 'bg-blue-100 text-blue-800',
      [ActionTypeEnum.CASE_ASSIGN_LIFECYCLE_WORKFLOW]: 'bg-purple-100 text-purple-800',
      [ActionTypeEnum.SEND_APPOINTMENT_BOOKING_LINK_TO_CLIENT]: 'bg-green-100 text-green-800',
      [ActionTypeEnum.SEND_CLIENT_TEMPLATE_EMAIL]: 'bg-teal-100 text-teal-800',
      [ActionTypeEnum.CASE_ASSIGN_LAWYER]: 'bg-orange-100 text-orange-800',
      [ActionTypeEnum.CASE_ASSIGN_LAW_DOMAIN]: 'bg-indigo-100 text-indigo-800',
      [ActionTypeEnum.CASE_ASSIGN_STATUS]: 'bg-yellow-100 text-yellow-800'
    }
    return colorMap[agentAction.value.action_type] || 'bg-gray-100 text-gray-800'
  })

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return new Intl.DateTimeFormat('de-DE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date)
  }

  const getDeliveryModeLabel = (mode: string) => {
    return mode === 'RECOMMENDATION' ? 'Recommendation' : 'Auto-Commit'
  }

  const getStatusInfo = computed(() => {
    if (!agentAction.value) return { label: 'Unknown', color: 'bg-gray-100 text-gray-600' }

    if (agentAction.value.is_reverted) {
      return { label: 'Reverted', color: 'bg-red-100 text-red-800' }
    }
    if (agentAction.value.has_commit_error) {
      return { label: 'Commit Error', color: 'bg-red-100 text-red-800' }
    }
    if (agentAction.value.is_committed) {
      return { label: 'Committed', color: 'bg-green-100 text-green-800' }
    }
    if (agentAction.value.is_committing) {
      return { label: 'Committing...', color: 'bg-yellow-100 text-yellow-800' }
    }
    return { label: 'Pending', color: 'bg-gray-100 text-gray-600' }
  })
</script>

<template>
  <div
    v-if="loading"
    class="flex items-center justify-center py-8"
  >
    <div class="loader"></div>
  </div>
  <div
    v-else-if="error"
    class="p-4 text-center text-red-500"
  >
    {{ error }}
  </div>
  <div
    v-else-if="agentAction"
    class="agent-action-feedback"
  >
    <div class="card rounded border p-4 shadow-sm">
      <h3 class="mb-3 text-lg font-bold">Agent Action Details</h3>

      <div class="grid grid-cols-1 gap-3">
        <!-- Action Type -->
        <div class="mb-2">
          <span class="font-semibold">Action Type:</span>
          <span
            class="ml-2 rounded-full px-3 py-1 text-sm font-medium"
            :class="actionTypeColor"
          >
            {{ getActionTypeDisplayName(agentAction.action_type) }}
          </span>
        </div>

        <!-- Status and Delivery Mode -->
        <div class="mb-2 flex flex-wrap gap-4">
          <div>
            <span class="font-semibold">Status:</span>
            <span
              class="ml-2 rounded-full px-2 py-1 text-sm"
              :class="getStatusInfo.color"
            >
              {{ getStatusInfo.label }}
            </span>
          </div>
          <div>
            <span class="font-semibold">Delivery Mode:</span>
            <span class="ml-2 text-sm text-gray-700">
              {{ getDeliveryModeLabel(agentAction.delivery_mode) }}
            </span>
          </div>
        </div>

        <!-- Agent Info -->
        <div class="mb-2">
          <span class="font-semibold">Agent:</span>
          <span class="ml-2 rounded bg-gray-100 px-2 py-1 text-sm">
            {{ agentAction.agent }}
          </span>
          <span
            v-if="agentAction.calling_agent"
            class="ml-2 text-sm text-gray-500"
          >
            (called by: {{ agentAction.calling_agent }})
          </span>
        </div>

        <!-- Reasoning -->
        <div
          v-if="agentAction.reasoning_translation"
          class="mb-2"
        >
          <div class="mb-1 font-semibold">Reasoning:</div>
          <div class="overflow-auto rounded border bg-gray-50 p-3">
            <p class="whitespace-pre-wrap text-sm">{{ agentAction.reasoning_translation }}</p>
          </div>
        </div>

        <!-- Commit Decision Reasoning -->
        <div
          v-if="agentAction.reasoning_commit_decision_translation"
          class="mb-2"
        >
          <div class="mb-1 font-semibold">Commit Decision Reasoning:</div>
          <div class="overflow-auto rounded border bg-gray-50 p-3">
            <p class="whitespace-pre-wrap text-sm">
              {{ agentAction.reasoning_commit_decision_translation }}
            </p>
          </div>
        </div>

        <!-- Payload -->
        <div
          v-if="agentAction.payload"
          class="mb-2"
        >
          <div class="mb-1 font-semibold">Payload:</div>
          <div class="max-h-48 overflow-auto rounded border bg-gray-50 p-3">
            <pre class="whitespace-pre-wrap text-xs">{{
              typeof agentAction.payload === 'string'
                ? agentAction.payload
                : JSON.stringify(agentAction.payload, null, 2)
            }}</pre>
          </div>
        </div>

        <!-- Commit Error -->
        <div
          v-if="agentAction.has_commit_error && agentAction.commit_error"
          class="mb-2"
        >
          <div class="mb-1 font-semibold text-red-600">Commit Error:</div>
          <div class="rounded border border-red-200 bg-red-50 p-3">
            <p class="whitespace-pre-wrap text-sm text-red-700">{{ agentAction.commit_error }}</p>
          </div>
        </div>

        <!-- Timestamps -->
        <div class="mt-2 grid grid-cols-2 gap-3 text-sm">
          <div>
            <span class="font-semibold">Created:</span>
            <span class="ml-2 text-gray-600">{{ formatDate(agentAction.created_at) }}</span>
          </div>
          <div v-if="agentAction.commited_at">
            <span class="font-semibold">Committed:</span>
            <span class="ml-2 text-gray-600">{{ formatDate(agentAction.commited_at) }}</span>
          </div>
          <div v-if="agentAction.reverted_at">
            <span class="font-semibold">Reverted:</span>
            <span class="ml-2 text-gray-600">{{ formatDate(agentAction.reverted_at) }}</span>
          </div>
        </div>

        <!-- Langfuse Link -->
        <div
          v-if="agentAction.langfuse_trace_url"
          class="mt-3 border-t pt-3"
        >
          <a
            :href="agentAction.langfuse_trace_url"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 hover:underline"
          >
            <ExternalLink class="h-4 w-4" />
            View in Langfuse
          </a>
        </div>

        <!-- UUID for debugging -->
        <div class="mt-2 text-xs text-gray-500">Agent Action ID: {{ agentAction.uuid }}</div>
      </div>
    </div>
  </div>
  <div
    v-else
    class="p-4 text-center text-gray-500"
  >
    No agent action data available
  </div>
</template>

<style scoped>
  .agent-action-feedback {
    max-width: 100%;
    overflow-x: auto;
  }

  .loader {
    border: 4px solid #f3f3f3;
    border-top: 4px solid #3498db;
    border-radius: 50%;
    width: 30px;
    height: 30px;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
</style>
